import { CheckoutContext } from '@/context/CheckoutContext';
import { Elements, ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import type {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import { useContext, useState } from 'react';
import { SkeletonLoader } from '../Common/SkeletonLoader';
import { EXPRESS_ELEMENTS_BASE_OPTIONS, LINK_PAYMENT_CONFIG } from '@/constants/stripe';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { useTranslate } from '@/hooks/useTranslate';
import { Loader } from '../Common/Loader';
import { PaymentMethodEnum } from '@/types/common';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import { logError } from '@/utils/logger';
import type { WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';

interface LinkButtonUIProps {
  isLoading: boolean;
  onReady: (event: StripeExpressCheckoutElementReadyEvent) => void;
  onConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  onLoadError: (error?: unknown) => void;
}

const LinkButtonUI = ({ isLoading, onReady, onConfirm, onLoadError }: LinkButtonUIProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { setError },
    payment: { isFormValid },
    customer,
  } = context;
  const { t } = useTranslate();

  const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
    if (isFormValid) {
      trackAndNotifyPaymentClick(context, { paymentMethodName: PaymentMethodEnum.LINK });
      event.resolve({ emailRequired: true });
    } else {
      const errorMessageKey = getZipCodeErrorMessageKey(customer);
      setError(t(errorMessageKey));
      event.reject();
    }
  };

  return (
    <div className="flex flex-col w-full min-h-11">
      <div className={`transition-opacity duration-500 ${!isLoading && 'hidden'}`}>
        <SkeletonLoader />
      </div>
      <div className={`transition-opacity duration-500 ${isLoading && 'hidden'}`}>
        <ExpressCheckoutElement
          onClick={handleClick}
          onConfirm={onConfirm}
          onReady={onReady}
          onLoadError={onLoadError}
          options={LINK_PAYMENT_CONFIG}
        />
      </div>
    </div>
  );
};

interface LinkButtonContentProps {
  onUnavailable: (reason: string) => void;
}

const LinkButtonContent = ({ onUnavailable }: LinkButtonContentProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { runPayment },
  } = context;
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(true);

  const handleLoadError = (error?: unknown) => {
    logError('Link load error', error);
    onUnavailable('load_error');
  };

  /**
   * Stripe ECE fires `onReady` even when Link is unavailable on this
   * device/browser — it just leaves the button unpainted. Per Stripe's type:
   *   availablePaymentMethods: undefined | AvailablePaymentMethods
   *   "...undefined if no payment methods can show."
   *
   * So unavailable manifests as EITHER:
   *   - `availablePaymentMethods === undefined`, OR
   *   - `availablePaymentMethods.link === false`
   */
  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    if (event.availablePaymentMethods?.link !== true) {
      onUnavailable('device_unsupported');
      return;
    }
    setTimeout(() => setIsLoading(false), 500);
  };

  // `runPayment` (CheckoutContent) handles and surfaces its own errors
  // internally and never rethrows, so there is nothing for a try/catch here
  // to ever catch.
  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    notifyPaymentIntentClick(context, { paymentMethodName: PaymentMethodEnum.LINK });
    // Express flag skips trackPaymentAttempt, which would emit a second button click.
    await runPayment({ event, elements: elements! }, PaymentMethodEnum.LINK);
  };

  return (
    <LinkButtonUI isLoading={isLoading} onReady={handleReady} onConfirm={handleConfirm} onLoadError={handleLoadError} />
  );
};

export const LinkPayForm = () => {
  const { pricing } = useContext(CheckoutContext);
  const [state, setState] = useState<WalletReadyState>('loading');
  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({ paymentMethod: PaymentMethodEnum.LINK });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUnavailable = (_reason: string) => {
    setState('unavailable');
  };

  if (state === 'unavailable') {
    return <PaymentMethodNotSupported />;
  }

  if (!isStripeLoading && !stripe) {
    return <PaymentMethodNotSupported />;
  }

  if (isStripeLoading || !stripe) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Elements
        stripe={stripe}
        options={{
          mode: 'payment',
          amount: Math.round(pricing.priceBreakdown.priceTotal),
          currency: pricing.currencyCode.toLowerCase(),
          loader: 'never',
          paymentMethodCreation: 'manual',
          ...EXPRESS_ELEMENTS_BASE_OPTIONS,
        }}
      >
        <InputTaxComponent />
        <LinkButtonContent onUnavailable={handleUnavailable} />
      </Elements>
    </div>
  );
};
